import csiAction from "csi/action";
import Action from "csi/def/Action";
import { Context } from "csi/def/Context";
import Heading from "csi/def/Heading";
import ViewRef from "csi/def/ViewRef";
import { AttributeMonitorContext } from "csi/heading/AttributeMonitor";
import Enum from "csi/heading/Enum";
import html from "csi/html";
import locale from "csi/locale";
import StoreCache from "csi/store/Cache";
import { C8Node, cnl } from "csi/typings";
import TextWithMaterialIcon from "csi/widget/TextWithMaterialIcon";
import xml from "csi/xml";
import declare from "dojo/_base/declare";
import _ from "underscore";
import {
	ENUM_APPROVED_WITH_CHANGES,
	ENUM_APPROVED,
	ENUM_FAILED,
	ENUM_REVIEW_COMPLETE,
	ENUM_UPLOADING,
	getIconParameters
} from "./ProofService";

function isProofLate(proofWorkflowInstance: C8Node, cache: StoreCache): boolean {
	if (!proofWorkflowInstance) {
		return false;
	}

	const stage = cache.getNode(proofWorkflowInstance.Subprocesses[0]);
	if (!stage) {
		return false;
	}

	const dueDate = cache.getNode(stage.Tasks[0]).DueDate;
	if (!dueDate) {
		return false;
	}

	const dateNow = Date.now();
	const bLate = dueDate < dateNow;
	return bLate;
}

export = declare(Enum, {
	/**
	 * @override
	 * Redraw the view when updated
	 */
	onMonitor(amc: AttributeMonitorContext, newValue) {
		amc.ctx.view.refreshData();
		return true;
	},


	/**
	 * @override
	 * Monitor status while uploading is in progress
	 */
	_getProgressBarStatus(node: C8Node, h: Heading) {
		return (node[h.m_id] == "ProofDecisionStatus:Uploading") ? "" : null;
	},

	/**
	 * @override
	 * Add any dependency paths required by renderer class
	 */
	 addRendererDependencyPaths(h: Heading, paths: string[]) {
		paths.push("Child:ProofWorkflow/Child:Subprocesses/Child:Tasks");
	},

	/**
	 * @override
	 * Draw status icon and name, pill "Late" and popup button
	 */
	toHtmlStatic(ctx: Context, h: Heading, refNode: HTMLElement, _params) {
		const enumValue = this._getValue(ctx, h);
		const value = xml.enumGetText(enumValue);

		// Do not render if value is not yet defined
		if (!value) {
			return true;
		}

		const revisionNode = ctx.getNode();

		// status icon and status text
		new TextWithMaterialIcon(getIconParameters(value, false, true, this.toValue(ctx, h)))
			.placeAt(refNode);

		const cache = ctx.cache;
		const proofWorkflowInstance = cache.getNode(revisionNode.ProofWorkflow);
		const bLate = isProofLate(proofWorkflowInstance, cache);

		// late pill
		if (bLate && (value != ENUM_APPROVED_WITH_CHANGES) && (value != ENUM_APPROVED) && (value != ENUM_REVIEW_COMPLETE)) {
			html.create("span", {
				class: "csi-proofing-status-late",
				innerText: locale.resource("StateLate")
					.toUpperCase(),
			}, refNode);
		}

		if (revisionNode.ProofIsLocked) {
			new TextWithMaterialIcon(getIconParameters("Locked", true, false))
				.placeAt(refNode);
		}

		// proof progress popup button
		if (proofWorkflowInstance && (value != ENUM_FAILED) && (value != ENUM_UPLOADING)) {
			new TextWithMaterialIcon({
				class: "csi-flex-inline",
				icon: (ctx.bExcel || ctx.bPrint) ? "" : "open_in_new",
				iconOnly: true,
				onClick: (): void => {
					const action = _.extend(new Action(), {
						m_id: "PopupProofProgress",
						m_viewRef: _.extend(new ViewRef(), {
							m_id: "ProofProgressPopUp",
							m_url: proofWorkflowInstance.$URL,
						}),
					});

					action.resolve();

					csiAction.invoke(ctx.view, refNode, action);
				},
			})
				.placeAt(refNode);

			return true;
		}
	},
});
